import { computed, ref } from 'vue';
import axios from 'axios';

/**
 * Filtros de cabecera de una tabla.
 *
 * Mantiene el estado de los filtros activos, los envía combinados al endpoint
 * (`filters[campo]=valor`, que la API aplica en AND) y devuelve el resultado a
 * la vista, que es quien sabe mapear sus propias filas.
 *
 * Los campos de texto esperan a que el usuario deje de escribir; los
 * desplegables y las fechas disparan la búsqueda al momento.
 */
export function useTableFilters({
  endpoint,
  perPage = 10,
  extraParams = () => ({}),
  onResults,
  onCleared,
  debounceTime = 450,
  translate,
}) {
  const filters = ref({});
  const filterError = ref('');
  const isLoading = ref(false);

  let debounceTimer = null;
  let lastController = null;

  const activeFilters = computed(() =>
    Object.fromEntries(
      Object.entries(filters.value).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    )
  );

  const isFiltering = computed(() => Object.keys(activeFilters.value).length > 0);

  const label = (key, fallback, params) => (translate ? translate(key, params ?? {}) : fallback);

  function messageFor(error) {
    const status = error?.response?.status;

    if (status === 422) return label('search.unsupportedFilter', 'No se puede filtrar por ese campo.', { filter: '' });
    if (status === 401 || status === 403) return label('search.forbidden', 'No tienes permiso para hacer esta búsqueda.');

    return label('search.failed', 'No se ha podido completar la búsqueda.');
  }

  const resolveEndpoint = () => (typeof endpoint === 'function' ? endpoint() : endpoint);

  async function runSearch(page = 1) {
    if (!isFiltering.value) {
      filterError.value = '';
      onCleared?.();

      return;
    }

    if (lastController) lastController.abort();
    lastController = new AbortController();

    // Se serializa a mano para no depender del formato que elija axios.
    const params = { per_page: perPage, page };

    // Un parámetro vacío no es "sin filtro" para la API: varias validaciones
    // usan `in:` y un '' las hace fallar con un 422.
    Object.entries(extraParams()).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) params[key] = value;
    });

    Object.entries(activeFilters.value).forEach(([field, value]) => {
      params[`filters[${field}]`] = value;
    });

    isLoading.value = true;

    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}${resolveEndpoint()}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params,
        signal: lastController.signal,
      });

      filterError.value = '';
      onResults?.(response.data);
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.message === 'canceled') return;

      filterError.value = messageFor(error);
      console.error('Error al filtrar la tabla:', error);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Lo llama la fila de filtros. `immediate` viene en true desde desplegables y
   * fechas, donde esperar no aporta nada.
   */
  function onFilterChange({ immediate = false } = {}) {
    if (debounceTimer) clearTimeout(debounceTimer);

    if (immediate) {
      runSearch(1);

      return;
    }

    debounceTimer = setTimeout(() => runSearch(1), debounceTime);
  }

  function clearFilters() {
    filters.value = {};
    filterError.value = '';
    if (debounceTimer) clearTimeout(debounceTimer);
    onCleared?.();
  }

  function dispose() {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (lastController) {
      try { lastController.abort(); } catch { /* la petición ya había terminado */ }
      lastController = null;
    }
  }

  return { filters, activeFilters, isFiltering, filterError, isLoading, runSearch, onFilterChange, clearFilters, dispose };
}
